/**
 * JSON-LD structured data for the public /pricing page.
 *
 * Emits three schemas as a single `<script type="application/ld+json">`, all
 * built from the same display-only config the page renders, so the markup can
 * never drift from the visible copy:
 *
 *   - FAQPage      — from PRICING_FAQ (real, on-page Q&A → eligible for FAQ
 *                    rich results).
 *   - SoftwareApplication + AggregateOffer — the four public tiers with their
 *                    monthly euro prices, low/high bounds and per-plan Offers.
 *   - BreadcrumbList — Home → Pricing.
 *
 * Server component → renders into the initial HTML so crawlers see it without
 * executing JS. We omit any field we can't fill truthfully (no reviews /
 * ratings are fabricated) and all URLs are absolute via the SEO config.
 */
import { SITE_NAME, DEFAULT_SITE_URL, absoluteUrl, OG_IMAGE } from "@/lib/seo";
import { PRICING_PLANS, PRICING_FAQ } from "@/lib/marketing-pricing";
import { DEFAULT_LOCALE } from "@/i18n/config";
import { makeTranslator } from "@/i18n/translate";

export function buildPricingJsonLd(): Record<string, unknown> {
  const pricingUrl = absoluteUrl("/pricing");

  // Display copy in the config is now i18n key paths; resolve to the default
  // locale (English) so the server-rendered JSON-LD stays the canonical copy
  // crawlers index, independent of the visitor's locale.
  const t = makeTranslator(DEFAULT_LOCALE);

  const faqPage = {
    "@type": "FAQPage",
    "@id": `${pricingUrl}#faq`,
    mainEntity: PRICING_FAQ.map((f) => ({
      "@type": "Question",
      name: t(f.q),
      acceptedAnswer: { "@type": "Answer", text: t(f.a) },
    })),
  };

  // Only tiers with a concrete monthly euro price become priced Offers;
  // Enterprise ("From €1,500 / Custom") still contributes its floor to the
  // aggregate bounds but is not asserted as a fixed-price offer.
  const pricedPlans = PRICING_PLANS.filter((p) => p.monthly != null);
  const prices = pricedPlans.map((p) => p.monthly as number);
  const lowPrice = Math.min(...prices);
  const highPrice = Math.max(...prices);

  const offers = PRICING_PLANS.filter((p) => p.monthly != null).map((p) => {
    // Per-plan price specs: the monthly rate, plus the annual rate where the
    // plan offers one. unitCode MON/ANN are the UN/CEFACT billing periods.
    const priceSpecification: Record<string, unknown>[] = [
      {
        "@type": "UnitPriceSpecification",
        price: String(p.monthly),
        priceCurrency: "EUR",
        valueAddedTaxIncluded: false,
        referenceQuantity: { "@type": "QuantitativeValue", value: 1, unitCode: "MON" },
      },
    ];
    if (p.annual != null) {
      priceSpecification.push({
        "@type": "UnitPriceSpecification",
        price: String(p.annual),
        priceCurrency: "EUR",
        valueAddedTaxIncluded: false,
        referenceQuantity: { "@type": "QuantitativeValue", value: 1, unitCode: "ANN" },
      });
    }
    return {
      "@type": "Offer",
      name: t(p.name),
      price: String(p.monthly),
      priceCurrency: "EUR",
      // Prices are quoted excl. VAT (EU B2B).
      valueAddedTaxIncluded: false,
      url: pricingUrl,
      category: "subscription",
      eligibleCustomerType: "Business",
      priceSpecification,
    };
  });

  const application = {
    "@type": "SoftwareApplication",
    // Same node identity as the homepage WebApplication so crawlers merge them.
    "@id": `${DEFAULT_SITE_URL}/#webapplication`,
    name: SITE_NAME,
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    url: DEFAULT_SITE_URL,
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: "EUR",
      lowPrice: String(lowPrice),
      highPrice: String(highPrice),
      offerCount: PRICING_PLANS.length,
      offers,
    },
  };

  const breadcrumb = {
    "@type": "BreadcrumbList",
    "@id": `${pricingUrl}#breadcrumb`,
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: DEFAULT_SITE_URL },
      { "@type": "ListItem", position: 2, name: "Pricing", item: pricingUrl },
    ],
  };

  const webPage = {
    "@type": "WebPage",
    "@id": `${pricingUrl}#webpage`,
    url: pricingUrl,
    name: "Pricing",
    inLanguage: DEFAULT_LOCALE,
    isPartOf: { "@id": `${DEFAULT_SITE_URL}/#webapplication` },
    breadcrumb: { "@id": `${pricingUrl}#breadcrumb` },
    primaryImageOfPage: OG_IMAGE.url,
  };

  return {
    "@context": "https://schema.org",
    "@graph": [faqPage, application, breadcrumb, webPage],
  };
}

export function PricingStructuredData() {
  const jsonLd = buildPricingJsonLd();
  return (
    <script
      type="application/ld+json"
      // Static, server-built object — no user input — so this is safe.
      dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
    />
  );
}
